import { useCallback, useEffect, useRef, useState } from "react";
import * as Y from "yjs";
import { useAnmeldung } from "../zustand/Anmeldung";
import { farbeFuer } from "../bausteine/Textwerk";
import {
  ausBase64,
  beruehreDokument,
  holeDokument,
  holePraesenz,
  holeSchritte,
  meldeAbwesenheit,
  meldeAnwesenheit,
  sendeSchritt,
  verdichte,
} from "../dienst/server";
import { erzeugeKanal } from "../dienst/echtzeit";

/*
  Eine offene Sitzung an einem Dokument.

  Hier steckt alles, was nichts mit dem Inhalt zu tun hat und für jede Art
  von Dokument gleich ist — Text wie Präsentation:

    laden      verdichteter Stand + alle Schritte, die jünger sind
    senden     eigene Änderungen, gebündelt, mit Wiederholung bei Fehlern
    empfangen  fremde Änderungen über den Echtzeitkanal
    Anwesenheit  wer ist da und wo steht seine Schreibmarke
    verdichten   beim Verlassen zusammenfassen

  Das stand ursprünglich vollständig in `Textdokument.js`. Als die
  Präsentationen dazukamen, hätte es dort ein zweites Mal stehen müssen —
  und jeder Fehler wäre an zwei Stellen zu beheben gewesen. Deshalb liegt
  es jetzt hier.

  Was NICHT hierher gehört: alles über den Inhalt. Ob im Dokument Absätze
  oder Folien stehen, weiß dieser Haken nicht und soll es nicht wissen.
*/

/*
  Wie lange Tastendrücke gesammelt werden, bevor sie zum Server gehen.

  Ohne das Bündeln entstünde je Tastendruck ein Datensatz — an einem Abend
  Tausende. Eine Drittelsekunde ist kurz genug, dass es sich für den anderen
  wie Echtzeit anfühlt, und lang genug, dass ein getipptes Wort in einem
  Stück geht.
*/
const BUENDELN_MS = 300;

/* Lebenszeichen, und ab wann jemand als weg gilt. */
const LEBENSZEICHEN_MS = 10000;
const VERGESSEN_MS = 30000;

/*
  Die eigene Schreibmarke wird höchstens alle 600 Millisekunden gemeldet.
  Ungebremst ginge bei jeder Mausbewegung ein Schreibvorgang an die
  Datenbank.
*/
const MARKE_MS = 600;

/* Ab wie vielen Schritten sich das Verdichten lohnt. */
const VERDICHTEN_AB = 20;

export default function useDokumentSitzung(dokumentId) {
  const { token, konto } = useAnmeldung();

  const [satz, setzeSatz] = useState(null);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState("");
  const [praesenz, setzePraesenz] = useState([]);
  const [bereit, setzeBereit] = useState(false);

  const docRef = useRef(null);
  const praesenzSatzRef = useRef(null);
  const auswahlRef = useRef(null);
  const markenUhrRef = useRef(null);
  /* Wann zuletzt gemeldet wurde, dass hier geschrieben wird. */
  const beruehrtRef = useRef(0);
  /* Was gesendet werden will, aber noch gebündelt wird. */
  const wartendRef = useRef([]);
  const buendelUhrRef = useRef(null);
  /* Bis wohin wir geladen haben — für das Nachladen nach einem Abbruch. */
  const standBisRef = useRef(null);
  const gesehenRef = useRef(new Set());
  const binErstellerRef = useRef(false);

  if (!docRef.current) docRef.current = new Y.Doc();
  const doc = docRef.current;

  /* ---------- Schritte einspielen ---------- */

  const spieleEin = useCallback(
    (schritte) => {
      const neue = schritte.filter((s) => !gesehenRef.current.has(s.id));
      if (neue.length === 0) return;

      doc.transact(() => {
        for (const schritt of neue) {
          gesehenRef.current.add(schritt.id);
          try {
            Y.applyUpdate(doc, ausBase64(schritt.daten), "server");
          } catch {
            /*
              Ein unlesbarer Schritt darf nicht das ganze Dokument
              aufhalten. Yjs verträgt Lücken — was fehlt, kommt beim
              nächsten Verdichten wieder mit.
            */
          }
        }
      }, "server");

      const juengster = neue[neue.length - 1];
      if (juengster && juengster.angelegt) standBisRef.current = juengster.angelegt;
    },
    [doc]
  );

  /* ---------- Laden ---------- */

  const laden = useCallback(async () => {
    setzeFehler("");
    try {
      const geladen = await holeDokument(token, dokumentId);
      setzeSatz(geladen);
      binErstellerRef.current = geladen.konto === konto.id;

      if (geladen.stand) {
        Y.applyUpdate(doc, ausBase64(geladen.stand), "server");
      }
      standBisRef.current = geladen.stand_bis || null;

      spieleEin(await holeSchritte(token, dokumentId, geladen.stand_bis));

      /*
        Erst JETZT gilt die Sitzung als bereit. Wer vorher etwas in das
        Dokument schreibt, bekommt es nicht hinausgeschickt: Der Horcher,
        der Änderungen sendet, hängt erst danach. Genau daran ist der erste
        Anlauf gescheitert — die Absätze des ersten Nutzers blieben in
        seinem Browser, und alle anderen sahen ein leeres Dokument.
      */
      setzeBereit(true);
    } catch (f) {
      setzeFehler(f.message);
    } finally {
      setzeLaedt(false);
    }
  }, [token, dokumentId, doc, spieleEin, konto]);

  useEffect(() => {
    laden();
  }, [laden]);

  /* ---------- Eigene Änderungen senden ---------- */

  useEffect(() => {
    if (!bereit) return undefined;

    async function abschicken() {
      const stapel = wartendRef.current;
      wartendRef.current = [];
      buendelUhrRef.current = null;
      if (stapel.length === 0) return;

      /*
        Mehrere Änderungen werden zu einer zusammengefasst — das ist
        weniger Verkehr und weniger Datensätze als jede einzeln.
      */
      const zusammen = Y.mergeUpdates(stapel);
      try {
        const geschrieben = await sendeSchritt(token, {
          dokumentId,
          fraktionId: konto.fraktion,
          kontoId: konto.id,
          daten: zusammen,
        });
        /*
          Der eigene Schritt kommt gleich über den Echtzeitkanal zurück.
          Vorgemerkt heißt: nicht noch einmal einspielen. Schaden nähme das
          Dokument nicht — Yjs verträgt Wiederholungen —, aber es wäre
          unnötige Arbeit bei jedem Tastendruck.
        */
        gesehenRef.current.add(geschrieben.id);
        if (geschrieben.angelegt) standBisRef.current = geschrieben.angelegt;

        /*
          Dem Dokument sagen, dass hier geschrieben wurde — sonst stünde es
          in Office weiter unten, obwohl gerade daran gearbeitet wird.
          Höchstens alle halbe Minute: Zu jedem gebündelten Tastendruck ein
          zweiter Schreibvorgang wäre die doppelte Last für nichts.
        */
        const jetzt = Date.now();
        if (jetzt - beruehrtRef.current > 30000) {
          beruehrtRef.current = jetzt;
          beruehreDokument(token, dokumentId);
        }
        /* Ging wieder — dann darf die Warnung auch wieder verschwinden. */
        setzeFehler((bisher) => (bisher.startsWith("Änderungen konnten") ? "" : bisher));
      } catch (f) {
        /*
          Nicht weggeworfen: zurück in die Warteschlange. Wer im Zug
          schreibt, verliert sonst genau das, was er gerade getippt hat.

          Und wirklich noch einmal versucht. Ohne die Uhr hier bliebe die
          Änderung liegen, bis der Nutzer zufällig weiterschreibt — bei
          jemandem, der gerade fertig war, also für immer. Der Satz
          „Es wird weiter versucht" wäre dann schlicht gelogen.
        */
        wartendRef.current = [zusammen, ...wartendRef.current];
        setzeFehler("Änderungen konnten nicht gesichert werden. Es wird weiter versucht.");
        if (!buendelUhrRef.current) {
          buendelUhrRef.current = setTimeout(abschicken, 3000);
        }
      }
    }

    function beiAenderung(aenderung, herkunft) {
      /* Was vom Server kam, geht nicht wieder dorthin zurück. */
      if (herkunft === "server") return;
      wartendRef.current.push(aenderung);
      if (buendelUhrRef.current) return;
      buendelUhrRef.current = setTimeout(abschicken, BUENDELN_MS);
    }

    doc.on("update", beiAenderung);

    return () => {
      doc.off("update", beiAenderung);
      if (buendelUhrRef.current) clearTimeout(buendelUhrRef.current);
      abschicken();
    };
  }, [bereit, doc, token, dokumentId, konto]);

  /* ---------- Fremde Änderungen empfangen ---------- */

  useEffect(() => {
    if (!bereit) return undefined;

    const kanal = erzeugeKanal(token);

    const nachladen = async () => {
      try {
        spieleEin(await holeSchritte(token, dokumentId, standBisRef.current));
        setzePraesenz(await bereitePraesenzAuf(token, dokumentId, konto.id));
      } catch {
        /* Beim nächsten Versuch wieder. */
      }
    };

    kanal.abonniere("dokumentschritte", (ereignis) => {
      const s = ereignis.record;
      if (!s || s.dokument !== dokumentId) return;
      spieleEin([s]);
    });

    kanal.abonniere("dokumentpraesenz", async (ereignis) => {
      const p = ereignis.record;
      if (!p || p.dokument !== dokumentId) return;
      setzePraesenz(await bereitePraesenzAuf(token, dokumentId, konto.id));
    });

    /*
      Nach einem Abbruch fehlt, was in der Stille geschah — der Strom holt
      nichts nach. Ohne dieses Nachladen fehlte am Ende Text, und niemand
      wüsste warum.
    */
    const wegNeu = kanal.beiWiederverbindung(nachladen);

    /* Kommt das Handy aus dem Ruhezustand, ist der Strom oft still gestorben. */
    const beiSichtbar = () => {
      if (document.visibilityState === "visible") nachladen();
    };
    document.addEventListener("visibilitychange", beiSichtbar);

    return () => {
      wegNeu();
      document.removeEventListener("visibilitychange", beiSichtbar);
      kanal.schliesse();
    };
  }, [bereit, token, dokumentId, konto, spieleEin]);

  /* ---------- Wer ist da ---------- */

  useEffect(() => {
    if (!bereit) return undefined;
    let laeuft = true;

    async function lebenszeichen() {
      if (!laeuft) return;
      try {
        const wo = auswahlRef.current;
        const geschrieben = await meldeAnwesenheit(token, {
          dokumentId,
          fraktionId: konto.fraktion,
          kontoId: konto.id,
          von: wo ? wo.von : 0,
          bis: wo ? wo.bis : 0,
          farbe: farbeFuer(konto.id),
          satzId: praesenzSatzRef.current,
        });
        praesenzSatzRef.current = geschrieben.id;
      } catch {
        /* Beim nächsten Mal wieder. */
      }
    }

    lebenszeichen();
    bereitePraesenzAuf(token, dokumentId, konto.id)
      .then((p) => laeuft && setzePraesenz(p))
      .catch(() => {});

    const uhr = setInterval(lebenszeichen, LEBENSZEICHEN_MS);

    return () => {
      laeuft = false;
      clearInterval(uhr);
      meldeAbwesenheit(token, praesenzSatzRef.current);
      praesenzSatzRef.current = null;
    };
  }, [bereit, token, dokumentId, konto]);

  /* ---------- Verdichten beim Verlassen ---------- */

  useEffect(() => {
    if (!bereit) return undefined;
    return () => {
      /*
        Das läuft beim Verlassen der Ansicht und darf scheitern — dann bleibt
        es beim nächsten Mal zu tun. Erst der Stand, dann das Wegräumen; nie
        umgekehrt.
      */
      (async () => {
        try {
          const schritte = await holeSchritte(token, dokumentId, null);
          if (schritte.length < VERDICHTEN_AB) return;
          await verdichte(token, dokumentId, {
            stand: Y.encodeStateAsUpdate(doc),
            bis: schritte[schritte.length - 1].angelegt,
            schritteIds: schritte.map((s) => s.id),
          });
        } catch {
          /* Beim nächsten Schließen. */
        }
      })();
    };
  }, [bereit, token, dokumentId, doc]);

  /* ---------- Die eigene Schreibmarke melden ---------- */

  /*
    Gedrosselt. Die Marke der anderen hinkt dafür um bis zu eine halbe
    Sekunde hinterher; bei Google ist sie sofort da. Für dreizehn Leute an
    einem Antrag ist das der richtige Tausch — PocketBase kennt keinen
    flüchtigen Kanal, jede Meldung ist ein Schreibvorgang.
  */
  const meldeMarke = useCallback(
    (auswahlJetzt) => {
      auswahlRef.current = auswahlJetzt;
      if (markenUhrRef.current) return;
      markenUhrRef.current = setTimeout(async () => {
        markenUhrRef.current = null;
        const wo = auswahlRef.current;
        try {
          const geschrieben = await meldeAnwesenheit(token, {
            dokumentId,
            fraktionId: konto.fraktion,
            kontoId: konto.id,
            von: wo ? wo.von : 0,
            bis: wo ? wo.bis : 0,
            farbe: farbeFuer(konto.id),
            satzId: praesenzSatzRef.current,
          });
          praesenzSatzRef.current = geschrieben.id;
        } catch {
          /* Beim nächsten Lebenszeichen wieder. */
        }
      }, MARKE_MS);
    },
    [token, dokumentId, konto]
  );

  return {
    doc,
    satz,
    setzeSatz,
    laedt,
    fehler,
    setzeFehler,
    bereit,
    /* Ohne einen selbst — wer die eigene Marke sieht, erschrickt. */
    andere: praesenz.filter((p) => p.kontoId !== konto.id),
    meldeMarke,
    binErsteller: binErstellerRef.current,
  };
}

/*
  Wer gerade im Dokument ist — ohne die, die längst weg sind.

  Der Server räumt nichts auf; wessen Browser abstürzt, dessen Satz bleibt
  stehen. Deshalb entscheidet hier die Zeit: Wer sich eine halbe Minute
  nicht gemeldet hat, ist weg.
*/
async function bereitePraesenzAuf(token, dokumentId, eigenesKonto) {
  const saetze = await holePraesenz(token, dokumentId);
  const jetzt = Date.now();
  return saetze
    .filter((p) => {
      if (p.konto === eigenesKonto) return false;
      const wann = new Date(p.gesehen).getTime();
      return Number.isFinite(wann) && jetzt - wann < VERGESSEN_MS;
    })
    .map((p) => ({
      kontoId: p.konto,
      /* Die Stelle im Text — ohne sie gäbe es keine Schreibmarke zu zeichnen. */
      von: Number(p.von) || 0,
      bis: Number(p.bis) || 0,
      farbe: p.farbe || "",
      name: (p.expand && p.expand.konto && p.expand.konto.name) || "Jemand",
    }));
}
